/**
 * @fileoverview useRunProgress
 * @description Owns one pipeline run: starts it, follows its progress, and
 *              exposes a display percentage that is smooth without being
 *              dishonest.
 *
 *              The run streams from the single request that executes it, so
 *              there is no run id to poll and nothing to reconcile: the
 *              connection *is* the run, and closing it cancels the work.
 *
 *              Two numbers drive the bar. `target` is the server's truth and
 *              only moves when real work completes. `creep` eases *within* the
 *              slice the active checkpoint owns, on a decaying curve that
 *              approaches but never reaches the slice boundary — so the bar
 *              keeps moving during a slow network call without ever claiming
 *              work that has not happened. The rendered value is the larger of
 *              the two and never decreases; a bar that jumps backwards reads
 *              as a failure even when the run is fine.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { crewPipelineCheckApi } from '../services/crewPipelineCheckApi';

/* How quickly the creep approaches its slice boundary. Larger = more patient. */
const CREEP_TIME_CONSTANT_MS = 4200;

/* The creep stops short of the boundary: the last sliver belongs to the server. */
const CREEP_CEILING = 0.9;

/* Per-frame easing toward the ceiling. ~12% closes a gap in about a third of a second. */
const EASE_FACTOR = 0.12;

/* The elapsed clock is read by a human, not a stopwatch. */
const ELAPSED_TICK_MS = 250;

const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export function useRunProgress() {
  const [run, setRun] = useState(null);
  const [events, setEvents] = useState([]);
  const [displayPercent, setDisplayPercent] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState('');
  const [starting, setStarting] = useState(false);

  const targetRef = useRef({ percent: 0, sliceStart: 0, sliceEnd: 0, enteredAt: 0, settled: true });
  const displayRef = useRef(0);
  const frameRef = useRef(null);
  const runningRef = useRef(false);
  const elapsedTickRef = useRef(0);
  const abortRef = useRef(null);
  const startedAtRef = useRef(0);
  const seqRef = useRef(0);

  const stopFollowing = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
  }, []);

  /* ── Animation loop ───────────────────────────────────────────────
     Runs only while there is something to animate. An always-on frame
     loop re-renders the whole result tree ~60 times a second for as long
     as the page is open, which costs gigabytes over a session. */
  const ensureAnimating = useCallback(() => {
    if (frameRef.current !== null) return;
    const reduced = prefersReducedMotion();

    const tick = () => {
      const { percent, sliceStart, sliceEnd, enteredAt, settled } = targetRef.current;

      let ceiling = percent;
      if (!settled && sliceEnd > sliceStart) {
        const age = performance.now() - enteredAt;
        const eased = 1 - Math.exp(-age / CREEP_TIME_CONSTANT_MS);
        ceiling = Math.max(
          ceiling,
          sliceStart + (sliceEnd - sliceStart) * eased * CREEP_CEILING,
        );
      }

      const current = displayRef.current;
      const next = reduced ? ceiling : current + (ceiling - current) * EASE_FACTOR;
      const clamped = Math.min(100, Math.max(current, next));

      if (clamped - current > 0.05) {
        displayRef.current = clamped;
        setDisplayPercent(clamped);
      }

      // The clock only needs to look live, so a few updates a second is
      // plenty and costs a fraction of the renders.
      const now = Date.now();
      if (runningRef.current && now - elapsedTickRef.current >= ELAPSED_TICK_MS) {
        elapsedTickRef.current = now;
        setElapsedMs(now - startedAtRef.current);
      }

      const atRest = ceiling - displayRef.current <= 0.05;
      if (!runningRef.current && atRest) {
        frameRef.current = null;
        return;
      }
      frameRef.current = requestAnimationFrame(tick);
    };

    frameRef.current = requestAnimationFrame(tick);
  }, []);

  useEffect(() => () => {
    if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
    frameRef.current = null;
  }, []);

  useEffect(() => stopFollowing, [stopFollowing]);

  const applyEvent = useCallback((event) => {
    if (!event || typeof event.seq !== 'number' || event.seq <= seqRef.current) return;
    seqRef.current = event.seq;

    const settled = event.type !== 'checkpoint.started' && event.type !== 'compare.started';
    targetRef.current = {
      percent: event.percent ?? targetRef.current.percent,
      sliceStart: event.sliceStart ?? 0,
      sliceEnd: event.sliceEnd ?? 0,
      enteredAt: settled ? targetRef.current.enteredAt : performance.now(),
      settled,
    };

    setEvents((prev) => [...prev, event]);
    setRun((prev) =>
      prev
        ? {
            ...prev,
            progress: { ...prev.progress, ...event, activeCheckpointId: event.checkpointId ?? null },
            checkpointStates: applyCheckpointState(prev.checkpointStates, event),
          }
        : prev,
    );
  }, []);

  const applyRun = useCallback((next) => {
    if (!next) return;
    setRun((prev) => ({ ...(prev || {}), ...next }));
    if (TERMINAL.has(next.status)) {
      targetRef.current = { ...targetRef.current, percent: 100, settled: true };
      runningRef.current = false;
      if (startedAtRef.current) setElapsedMs(Date.now() - startedAtRef.current);
    }
  }, []);

  const start = useCallback(
    async (payload) => {
      stopFollowing();
      seqRef.current = 0;
      displayRef.current = 0;
      targetRef.current = { percent: 0, sliceStart: 0, sliceEnd: 0, enteredAt: 0, settled: true };
      startedAtRef.current = Date.now();
      elapsedTickRef.current = 0;
      runningRef.current = true;
      setDisplayPercent(0);
      setElapsedMs(0);
      setEvents([]);
      setError('');
      setRun(null);
      setStarting(true);
      ensureAnimating();

      try {
        const controller = new AbortController();
        abortRef.current = controller;
        await crewPipelineCheckApi.runFlow(payload, {
          onAccepted: (doc) => setRun(doc),
          onEvent: applyEvent,
          onDone: applyRun,
          signal: controller.signal,
        });
      } catch (err) {
        // Aborting is how the user cancels, so it is not a failure to report.
        if (err.name !== 'AbortError') setError(err.message || 'The run could not be completed.');
        startedAtRef.current = 0;
      } finally {
        runningRef.current = false;
        setStarting(false);
      }
    },
    [applyEvent, applyRun, ensureAnimating, stopFollowing],
  );

  const cancel = useCallback(() => {
    // The run lives inside the open request, so hanging up ends it.
    stopFollowing();
    runningRef.current = false;
    setRun((prev) => (prev ? { ...prev, status: 'cancelled' } : prev));
  }, [stopFollowing]);

  const reset = useCallback(() => {
    stopFollowing();
    runningRef.current = false;
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    startedAtRef.current = 0;
    seqRef.current = 0;
    displayRef.current = 0;
    targetRef.current = { percent: 0, sliceStart: 0, sliceEnd: 0, enteredAt: 0, settled: true };
    setRun(null);
    setEvents([]);
    setDisplayPercent(0);
    setElapsedMs(0);
    setError('');
  }, [stopFollowing]);

  const active = Boolean(run) && !TERMINAL.has(run.status);

  return {
    run,
    events,
    error,
    starting,
    active,
    displayPercent,
    elapsedMs,
    start,
    cancel,
    reset,
  };
}

/** Fold one event into the per-checkpoint status map. */
function applyCheckpointState(states = {}, event) {
  if (!event.checkpointId) return states;
  const existing = states[event.checkpointId] || {};
  if (event.type === 'checkpoint.started') {
    return { ...states, [event.checkpointId]: { ...existing, status: 'running' } };
  }
  if (event.type === 'checkpoint.completed') {
    return {
      ...states,
      [event.checkpointId]: {
        ...existing,
        status: event.status === 'error' ? 'error' : 'completed',
        durationMs: event.durationMs,
        crewCount: event.crewCount,
        error: event.status === 'error' ? event.message : null,
      },
    };
  }
  return states;
}

export default useRunProgress;
